"use client";

import Link from "next/link";
import { useState } from "react";
import { Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { AppLogo } from "@/components/brand/app-logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

type SiteHeaderProps = {
  /** Sits on dark landing hero: light chrome, primary CTA. */
  variant?: "default" | "darkHero";
};

export function SiteHeader({ variant = "default" }: SiteHeaderProps) {
  const dark = variant === "darkHero";
  /** Sheet must be controlled so tapping a link can close it; otherwise the menu stays open after navigation. */
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const mutedLink = cn(
    "rounded-md px-3 py-2.5 text-sm font-medium transition-colors sm:py-2",
    dark
      ? "text-slate-300 hover:bg-white/10 hover:text-white"
      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
  );

  /**
   * Mobile menu panel always renders on the light `bg-background` sheet regardless of
   * header variant, so don't switch links to white text. The earlier `dark` branch
   * produced white text on a white sheet — only the X, "Menu" title and primary CTA
   * were legible.
   */
  const mobileSheetLink =
    "flex min-h-12 w-full items-center rounded-lg border border-slate-200 bg-white px-4 py-3 text-base font-medium text-slate-900 transition-colors touch-manipulation hover:bg-slate-50";

  const mobileSheetPrimary = cn(
    "flex min-h-12 w-full items-center justify-center rounded-lg px-4 py-3 text-base font-semibold shadow-sm transition-colors touch-manipulation",
    "bg-primary text-primary-foreground hover:bg-primary/90"
  );

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full pt-[env(safe-area-inset-top,0px)] backdrop-blur-xl",
        dark
          ? "border-b border-white/10 bg-slate-950/85 text-slate-100"
          : "border-b border-slate-200/80 bg-white/95 text-slate-900"
      )}
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-4 sm:h-16 sm:px-6 lg:px-8">
        <AppLogo href="/" tone={dark ? "marketingDark" : "marketing"} />
        <nav className="hidden items-center gap-1 sm:flex sm:gap-2" aria-label="Primary">
          <Link href="/#product" className={mutedLink}>
            Product
          </Link>
          <Link href="/#pricing" className={mutedLink}>
            Pricing
          </Link>
          <Link href="/guides" className={mutedLink}>
            Guides
          </Link>
          <Link href="/contact" className={mutedLink}>
            Contact
          </Link>
          <Link href="/login?force=1" className={mutedLink}>
            Sign in
          </Link>
          <Link
            href="/signup"
            className="ml-1 inline-flex min-h-11 items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
          >
            Get started
          </Link>
        </nav>

        <div className="flex items-center gap-1 sm:hidden">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn("shrink-0 touch-manipulation", dark && "text-slate-100 hover:bg-white/10 hover:text-white")}
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" strokeWidth={2} />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="flex w-[min(100vw-1rem,22rem)] flex-col border-l bg-background pb-[max(1rem,env(safe-area-inset-bottom,0px))] pt-[env(safe-area-inset-top,0px)]"
            >
              <SheetHeader className="text-left">
                <SheetTitle className="text-lg">Menu</SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-1 flex-col gap-3" aria-label="Primary">
                <Link href="/#product" className={mobileSheetLink} onClick={closeMenu}>
                  Product
                </Link>
                <Link href="/#pricing" className={mobileSheetLink} onClick={closeMenu}>
                  Pricing
                </Link>
                <Link href="/guides" className={mobileSheetLink} onClick={closeMenu}>
                  Guides
                </Link>
                <Link href="/contact" className={mobileSheetLink} onClick={closeMenu}>
                  Contact
                </Link>
                <Link href="/login?force=1" className={mobileSheetLink} onClick={closeMenu}>
                  Sign in
                </Link>
                <Link href="/signup" className={mobileSheetPrimary} onClick={closeMenu}>
                  Get started
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
